import { AppUrlParams } from "@/Common/Api/ApiSettings";
import { SignatureHelper } from "@/Common/Helpers/SignatureHelper";
import { WebFile } from "@/Common/Models/WebFile";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface IFileUploadLiteProps {
    acceptedFormat?: string[];
    className?: string;
    showFilename?: boolean;
    isSignature?: boolean;
    uploadButtonText?: string;
    onCompleteOperation?: number;
    onCompleteCallback?: (path: string) => void;
    fileDestinationPath?: number;

    // PERMANENT_MEDICAL_FILES_ROOT = 0,
    // PERMANENT_MEDICAL_FILES = 1,
    // TEMPORARY_MEDICAL_FILES = 2,
    // PERMANENT_NON_MEDICAL_FILES = 3
}

class FileUploadLiteState {
    public file: WebFile;
}

interface IUnploadResponse {
    Path: string;
    Success: boolean;
    Errors: string[];
}

class FileUploadLite extends React.Component<IFileUploadLiteProps, FileUploadLiteState> {
    private controlId: string;
    private control: HTMLElement;

    public get hasFile(): boolean {
        return !!this.state.file;
    }

    constructor(props: IFileUploadLiteProps) {
        super(props);
        this.controlId = `FileUploadLite_${sessionStore.newId()}`;

        this.state = {
            file: null
        };
    }

    public componentDidMount(): void {
        this.control = document.getElementById(this.controlId + "_input");
        const self = this;

        this.control.addEventListener("change", function() {
            // "this" here refers to the windows attachment selection popup in which the user selected the files to attach. Do not use Self here.
            // @ts-ignore: Ignore error
            self.onChange(this.files);
        });
    }

    public componentWillUnmount(): void {
        this.control.removeEventListener("change", null);
    }

    public render(): JSX.Element {
        return (
            <div className={this.props.className} id={this.controlId}>
                {this.state.file && this.props.isSignature && !SignatureHelper.isValidFileFormat(this.state.file.type) && (
                    <div style={{ color: "red", marginBottom: "5px" }}>{cacheStore.GetString("InvalidSignatureFileFormat")}</div>
                )}
                <div style={{ cursor: "pointer" }}>
                    <input
                        id={this.controlId + "_input"}
                        type="file"
                        name="fn00"
                        value=""
                        accept={this.props.acceptedFormat ? this.props.acceptedFormat.toString() : "*"}
                        onChange={() => this.onChange}
                        style={{ display: "none" }}
                    />
                    <div className="upload-progress"><div className="bar" /></div>
                    <label className="link-label" htmlFor={this.controlId + "_input"}>{this.props.uploadButtonText || cacheStore.GetString("SelectImageLabel")}</label>
                </div>
                {(this.props.showFilename !== undefined || this.props.showFilename) && this.state.file && (
                    <div className="filename-display flex-container space-between">
                        <div className="file-name margin-none-left">{this.state.file.name}</div>
                        <div style={{ marginLeft: "5px" }}>
                            <a
                                style={{ color: "red", fontWeight: "bold" }}
                                onClick={() => this.removeFile()}
                                data-cy="file-upload-remove"
                            >X
                            </a>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private removeFile(): void {
        this.setState({
            file: null
        });

        this.control.nodeValue = "";
        this.props?.onCompleteCallback?.(null);
    }

    private isFileNameEncoded(fileName: string): boolean {
        try {
            return decodeURIComponent(fileName) !== fileName;
        }
        catch (e) {
            return false;
        }
    }

    private removeSpecialCharacters(fileName: string): string {
        return fileName.replace(/[^a-zA-Z0-9._-]/g, "");
    }

    private getHeaderFileName(fileName: string): string {
        let headerFileName = fileName;
        if (this.isFileNameEncoded(headerFileName)) {
            try {
                headerFileName = decodeURIComponent(headerFileName);
            }
            catch (e) {
                console.error(e);
            }
        }
        const cleanFileName = this.removeSpecialCharacters(headerFileName);
        return encodeURIComponent(cleanFileName);
    }

    private reportProgress(e: ProgressEvent): void {
        const control = $("#" + this.controlId);
        if (e.lengthComputable) {
            const cssWith = (e.loaded / e.total) * 100 + "%";
            control.find("div.bar").css("width", cssWith);
        }
        else
            control.find("div.upload-progress").css("display", "none");
    }

    private onChange(files: WebFile[]): void {
        this.setState({
            file: files[0]
        });

        return this.uploadFiles();
    }

    public uploadFiles(): void {
        if (this.state.file == null)
            return;

        if (this.props.isSignature && !SignatureHelper.isValidFileFormat(this.state.file.type))
            return;

        const ajaxRequest = new XMLHttpRequest();
        const headerFileName = this.getHeaderFileName(this.state.file.name);

        ajaxRequest.withCredentials = true;
        ajaxRequest.upload.addEventListener("progress", e => this.reportProgress(e));

        ajaxRequest.addEventListener("load", () => {
            const response: IUnploadResponse = JSON.parse(ajaxRequest.responseText);
            if (response.Success) {
                if (this.props.onCompleteOperation != null) {
                    const operation = new LocalEvent(this.props.onCompleteOperation);
                    operation.AddItem("path", response.Path);
                    LocalEventDispatcher.dispatch(operation);
                }

                this.props.onCompleteCallback && this.props.onCompleteCallback(response.Path);
            }

            if (response.Errors?.any()) {
                console.error(`error : ${JSON.stringify(response.Errors)}`);
                sessionStore.showPopup(response.Errors);
            }

            $("#" + this.controlId).find("div.upload-progress").css("display", "none");
            ajaxRequest.upload.removeEventListener("progress", null);
            ajaxRequest.upload.removeEventListener("load", null);
        });

        ajaxRequest.open("post", this.getUploadUrl(), true);
        ajaxRequest.setRequestHeader("X-File-Name", headerFileName);
        ajaxRequest.setRequestHeader("X-File-Size", String(this.state.file.size));
        ajaxRequest.setRequestHeader("X-File-Type", this.state.file.type);
        ajaxRequest.setRequestHeader("api-key", apiSettings.ApiKey);
        ajaxRequest.setRequestHeader("sessionId", apiSettings.SessionId);
        ajaxRequest.send(this.state.file);

        // display progress bar
        $("#" + this.controlId).find("div.upload-progress").css("display", "block");
    }

    private getUploadUrl(): string {
        const args: AppUrlParams = {};
        if (this.props.fileDestinationPath)
            args.destination = this.props.fileDestinationPath;
        if (this.props.isSignature)
            args.isSignature = true;

        return apiSettings.buildAppUrl("api/core/upload.json", { params: args });
    }
}
export { FileUploadLite };
